// src/components/settings/CreateUser.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserApi, OrganizationApi, RoleApi } from '../../services/ApiService';

function CreateUserPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!userId;
  
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form fields
  const [organizationId, setOrganizationId] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load user details
      const userResponse = await UserApi.getUser(userId);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setEmail(userData.email || '');
        setFirstName(userData.firstName || userData.first_name || '');
        setLastName(userData.lastName || userData.last_name || '');
        setIsActive(userData.isActive ?? userData.is_active ?? true);
        setOrganizationId(userData.organization?.organizationId || userData.organization?.organization_id || '');
      }

      // Load user roles
      const rolesResponse = await RoleApi.getUserRoles(userId);
      if (rolesResponse.ok) {
        const userRoles = await rolesResponse.json();
        const roleIds = userRoles.map(role => role.roleId || role.role_id);
        setSelectedRoles(roleIds);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      setMessage("Error loading user data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isEditing && userId) {
      loadUserData();
    }
  }, [userId, isEditing, loadUserData]);

  const loadInitialData = async () => {
    try {
      // Load organizations
      const orgResponse = await OrganizationApi.getOrganizations();
      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        setOrganizations(Array.isArray(orgData) ? orgData : []);
      }

      // Load roles
      const roleResponse = await RoleApi.getRoles();
      if (roleResponse.ok) {
        const roleData = await roleResponse.json();
        setRoles(Array.isArray(roleData) ? roleData : []);
      }

      // Load users for display
      const userResponse = await UserApi.getUsers();
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUsers(Array.isArray(userData) ? userData : []);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      setMessage("Error loading form data. Please try again.");
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const userData = {
        organization_id: organizationId,
        email,
        first_name: firstName,
        last_name: lastName,
        is_active: isActive
      };

      if (!isEditing) {
        userData.password = password;
      }

      let response;
      let newUserId;

      if (isEditing) {
        // Update existing user
        response = await UserApi.updateUser(userId, userData);
        newUserId = userId;
      } else {
        // Create new user
        response = await UserApi.createUser(userData);
      }

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `Failed to ${isEditing ? 'update' : 'create'} user.`);
      }

      const responseData = await response.json();
      if (!isEditing) {
        newUserId = responseData.user_id || responseData.userId || responseData.id;
      }

      // Handle role assignments
      if (newUserId && selectedRoles.length > 0) {
        const rolePromises = selectedRoles.map(roleId => 
          RoleApi.assignRoleToUser(newUserId, roleId)
        );
        await Promise.all(rolePromises);
      }

      setMessage(`User ${isEditing ? 'updated' : 'created'} successfully!`);
      
      if (!isEditing) {
        // Clear form for new user
        setOrganizationId('');
        setEmail('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setSelectedRoles([]);
        setIsActive(true);
        
        // Refresh users list
        loadInitialData();
      } else {
        // Redirect to user list after edit
        setTimeout(() => navigate('/settings/users'), 2000);
      }

    } catch (error) {
      console.error('Error saving user:', error);
      setMessage(error.message || `An error occurred while ${isEditing ? 'updating' : 'creating'} the user.`);
    } finally {
      setLoading(false);
    }
  };

  const getRoleTypeColor = (roleName) => {
    const roleColors = {
      'admin': '#dc3545',
      'moderator': '#fd7e14',
      'instructor': '#6f42c1',
      'student': '#20c997',
      'user': '#6c757d',
      'manager': '#007bff'
    };
    
    const lowerRoleName = roleName?.toLowerCase();
    for (const [role, color] of Object.entries(roleColors)) {
      if (lowerRoleName?.includes(role)) {
        return color;
      }
    }
    return '#6c757d';
  };

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>{isEditing ? 'Edit User' : 'Create New User'}</h2>
      </div>

      {!isEditing && users.length > 0 && (
        <div className="info-panel" style={{ marginBottom: '2rem' }}>
          <h4>Existing Users ({users.length}):</h4>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {users.slice(0, 10).map((user) => (
              <div key={user.user_id || user.userId} style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {(user.firstName || user.first_name)} {(user.lastName || user.last_name)} ({user.email})
              </div>
            ))}
            {users.length > 10 && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>...and {users.length - 10} more</div>}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
        <div style={{ marginBottom: '1rem' }}>
          <label>Organization *</label>
          <select 
            value={organizationId} 
            onChange={(e) => setOrganizationId(e.target.value)} 
            required
            disabled={loading}
          >
            <option value="">Select organization</option>
            {organizations.map((org) => (
              <option key={org.organization_id || org.organizationId} value={org.organization_id || org.organizationId}>
                {org.name || org.organizationName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Email *</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            required 
            disabled={loading}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label>First Name *</label>
            <input 
              type="text" 
              value={firstName} 
              onChange={(e) => setFirstName(e.target.value)} 
              required 
              disabled={loading}
            />
          </div>
          <div>
            <label>Last Name *</label>
            <input 
              type="text" 
              value={lastName} 
              onChange={(e) => setLastName(e.target.value)} 
              required 
              disabled={loading}
            />
          </div>
        </div>

        {!isEditing && (
          <div style={{ marginBottom: '1rem' }}>
            <label>Password *</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              disabled={loading}
            />
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input 
              type="checkbox" 
              checked={isActive} 
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={loading}
            />
            User is active
          </label>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Assign Roles</label>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '0.5rem',
            padding: '0.5rem',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            backgroundColor: 'var(--card-background)'
          }}>
            {roles.map((role) => (
              <label key={role.roleId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input 
                  type="checkbox"
                  checked={selectedRoles.includes(role.roleId)}
                  onChange={() => handleRoleToggle(role.roleId)}
                  disabled={loading}
                />
                <span 
                  style={{
                    padding: '0.125rem 0.375rem',
                    backgroundColor: getRoleTypeColor(role.roleName),
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}
                >
                  {role.roleName}
                </span>
              </label>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
        </button>
      </form>

      {message && (
        <div className={message.includes('successfully') ? 'success-message' : 'error-message'} style={{ marginTop: '1rem' }}>
          {message}
        </div>
      )}
    </div>
  );
}

export default CreateUserPage;
